import React, { useState } from "react";
import { requestCatalogueLinkExtension } from "../../../components/catalogue/catalogueSecurityApi";

type Props = {
  catalogueId: string;
  linkToken: string;
  hostLabel: string;
  /** Open link without an invitee name: ask who is asking. */
  needsName: boolean;
  alreadyRequested: boolean;
  declined: boolean;
};

type Phase = "idle" | "sent" | "declined";

/**
 * Shown on an expired link: the buyer asks the studio to reopen this same link. The studio
 * approves 7, 30 or 90 days, and the link they already have works again.
 */
const PublicLinkExtensionRequest: React.FC<Props> = ({
  catalogueId,
  linkToken,
  hostLabel,
  needsName,
  alreadyRequested,
  declined,
}) => {
  const [phase, setPhase] = useState<Phase>(alreadyRequested ? "sent" : declined ? "declined" : "idle");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (needsName && !name.trim()) {
      setError("Enter your name.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const json = await requestCatalogueLinkExtension(
        catalogueId,
        linkToken,
        needsName ? { name: name.trim(), phone: phone.trim() || undefined } : {}
      ).catch(() => ({ success: false, message: "Could not reach the server. Check the connection and try again." }));
      const status = json?.success ? (json as { data?: { status?: string } }).data?.status : undefined;
      if (status === "SENT" || status === "ALREADY_SENT") setPhase("sent");
      else if (status === "DECLINED") setPhase("declined");
      else setError(json?.message || "Could not send the request. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const Host = hostLabel ? hostLabel.charAt(0).toUpperCase() + hostLabel.slice(1) : "The studio";

  if (phase === "sent") {
    return (
      <div className="hkp-shared-gate" data-testid="link-extension-sent" role="status">
        <h2>Request sent</h2>
        <p className="hkp-shared-gate-lede">
          {Host} will see it. Once they reopen it, this same link works again. Keep it.
        </p>
      </div>
    );
  }

  if (phase === "declined") {
    return (
      <div className="hkp-shared-gate" data-testid="link-extension-declined" role="status">
        <h2>This link has expired</h2>
        <p className="hkp-shared-gate-lede">
          {Host} did not reopen it this time. You can ask again tomorrow, or contact them directly.
        </p>
      </div>
    );
  }

  return (
    <div className="hkp-shared-gate" data-testid="link-extension-request">
      <h2>This link has expired</h2>
      <p className="hkp-shared-gate-lede">
        Ask {hostLabel} to reopen it. You keep this same link; no new one is needed.
      </p>
      <form onSubmit={(e) => void submit(e)}>
        {needsName ? (
          <>
            <label htmlFor="hkp-ext-name">Your name</label>
            <input
              id="hkp-ext-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={80}
            />
            <label htmlFor="hkp-ext-mobile">Mobile (optional)</label>
            <input
              id="hkp-ext-mobile"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile, or +country code"
            />
          </>
        ) : null}
        {error ? (
          <p className="hkp-shared-gate-hint" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="hkp-shared-gate-submit" disabled={busy}>
          {busy ? "Sending…" : "Request extension"}
        </button>
      </form>
    </div>
  );
};

export default PublicLinkExtensionRequest;
